import React, { useState, useEffect } from 'react'
import { Button } from '../Button'
import { api } from '../../services/api'

export interface PackageFormData {
  name: string
  slug: string
  description: string
  short_description: string
  package_type: string
  billing_type: string
  setup_fee?: number
  monthly_price?: number
  yearly_price?: number
  one_time_price?: number
  features: PackageFeature[]
  technical_specs: TechnicalSpecs
  services_included?: ServiceConfig[]
  includes_google_workspace?: boolean
  google_workspace_tier?: string
  is_featured: boolean
  is_popular: boolean
  is_available: boolean
  is_active: boolean
  badge_text?: string
  image_url?: string
}

export interface PackageFeature {
  title: string
  description: string
  included: boolean
  highlighted: boolean
}

export interface TechnicalSpecs {
  cpu_cores?: number
  ram_gb?: number
  storage_gb?: number
  bandwidth_gb?: number
  ssl_included?: boolean
  backup_included?: boolean
  monitoring_included?: boolean
}

export interface ServiceConfig {
  name: string
  image: string
  ports?: number[]
  environment?: Record<string, string>
  volumes?: string[]
}

interface PackageFormProps {
  initialData?: Partial<PackageFormData>
  onSubmit: (data: PackageFormData) => Promise<void>
  onCancel: () => void
  isEditing?: boolean
  loading?: boolean
}

const packageTypes = [
  { value: 'AI_CORPORATE', label: 'AI Corporate' },
  { value: 'MEDIA', label: 'Media' },
  { value: 'WEBSITE_HOSTING', label: 'Website Hosting' },
  { value: 'FULL_STACK', label: 'Full Stack' },
  { value: 'HARDWARE_BUNDLE', label: 'Hardware Bundle' },
  { value: 'CUSTOM', label: 'Custom' },
]

const billingTypes = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' },
  { value: 'ONE_TIME', label: 'One Time' },
  { value: 'CUSTOM', label: 'Custom' },
]

const googleWorkspaceTiers = [
  { value: 'business', label: 'Business' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'starter', label: 'Starter' },
]

export function PackageForm({
  initialData,
  onSubmit,
  onCancel,
  isEditing = false,
  loading = false
}: PackageFormProps) {
  const [formData, setFormData] = useState<PackageFormData>({
    name: '',
    slug: '',
    description: '',
    short_description: '',
    package_type: 'FULL_STACK',
    billing_type: 'MONTHLY',
    features: [],
    technical_specs: {},
    is_featured: false,
    is_popular: false,
    is_available: true,
    is_active: true,
    ...initialData
  })

  const [currentFeature, setCurrentFeature] = useState<PackageFeature>({
    title: '',
    description: '',
    included: true,
    highlighted: false
  })

  const [currentService, setCurrentService] = useState<ServiceConfig>({
    name: '',
    image: '',
    ports: [],
    environment: {},
    volumes: []
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Auto-generate slug from name
  useEffect(() => {
    if (!isEditing && formData.name && !formData.slug) {
      const generatedSlug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      setFormData(prev => ({ ...prev, slug: generatedSlug }))
    }
  }, [formData.name, isEditing])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required'
    if (!formData.description.trim()) newErrors.description = 'Description is required'
    if (!formData.short_description.trim()) newErrors.short_description = 'Short description is required'

    // Validate pricing based on billing type
    if (formData.billing_type === 'MONTHLY' && !formData.monthly_price) {
      newErrors.monthly_price = 'Monthly price is required for monthly billing'
    }
    if (formData.billing_type === 'YEARLY' && !formData.yearly_price) {
      newErrors.yearly_price = 'Yearly price is required for yearly billing'
    }
    if (formData.billing_type === 'ONE_TIME' && !formData.one_time_price) {
      newErrors.one_time_price = 'One-time price is required for one-time billing'
    }

    // Validate slug format
    if (formData.slug && !/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const addFeature = () => {
    if (currentFeature.title && currentFeature.description) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, { ...currentFeature }]
      }))
      setCurrentFeature({
        title: '',
        description: '',
        included: true,
        highlighted: false
      })
    }
  }

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }))
  }

  const addService = () => {
    if (currentService.name && currentService.image) {
      setFormData(prev => ({
        ...prev,
        services_included: [
          ...(prev.services_included || []),
          { ...currentService }
        ]
      }))
      setCurrentService({
        name: '',
        image: '',
        ports: [],
        environment: {},
        volumes: []
      })
    }
  }

  const removeService = (index: number) => {
    setFormData(prev => ({
      ...prev,
      services_included: prev.services_included?.filter((_, i) => i !== index) || []
    }))
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit Package' : 'Create New Package'}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Package Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className={`w-full rounded-md border ${errors.name ? 'border-red-300' : 'border-gray-300'} px-3 py-2 focus:border-blue-500 focus:ring-blue-500`}
              placeholder="Enter package name"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Slug *
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              className={`w-full rounded-md border ${errors.slug ? 'border-red-300' : 'border-gray-300'} px-3 py-2 focus:border-blue-500 focus:ring-blue-500`}
              placeholder="package-slug"
            />
            {errors.slug && <p className="text-red-500 text-sm mt-1">{errors.slug}</p>}
          </div>
        </div>

        {/* Package Type and Billing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Package Type *
            </label>
            <select
              value={formData.package_type}
              onChange={(e) => setFormData(prev => ({ ...prev, package_type: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
            >
              {packageTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Billing Type *
            </label>
            <select
              value={formData.billing_type}
              onChange={(e) => setFormData(prev => ({ ...prev, billing_type: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
            >
              {billingTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Descriptions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Short Description *
          </label>
          <input
            type="text"
            value={formData.short_description}
            onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
            className={`w-full rounded-md border ${errors.short_description ? 'border-red-300' : 'border-gray-300'} px-3 py-2 focus:border-blue-500 focus:ring-blue-500`}
            placeholder="Brief description for cards and listings"
          />
          {errors.short_description && <p className="text-red-500 text-sm mt-1">{errors.short_description}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Description *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={4}
            className={`w-full rounded-md border ${errors.description ? 'border-red-300' : 'border-gray-300'} px-3 py-2 focus:border-blue-500 focus:ring-blue-500`}
            placeholder="Detailed description of the package"
          />
          {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
        </div>

        {/* Pricing */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Pricing</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Setup Fee ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.setup_fee || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, setup_fee: e.target.value ? parseFloat(e.target.value) : undefined }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>

            {(formData.billing_type === 'MONTHLY' || formData.billing_type === 'CUSTOM') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Price ($) {formData.billing_type === 'MONTHLY' && '*'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.monthly_price || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, monthly_price: e.target.value ? parseFloat(e.target.value) : undefined }))}
                  className={`w-full rounded-md border ${errors.monthly_price ? 'border-red-300' : 'border-gray-300'} px-3 py-2 focus:border-blue-500 focus:ring-blue-500`}
                  placeholder="0.00"
                />
                {errors.monthly_price && <p className="text-red-500 text-sm mt-1">{errors.monthly_price}</p>}
              </div>
            )}

            {(formData.billing_type === 'YEARLY' || formData.billing_type === 'CUSTOM') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Yearly Price ($) {formData.billing_type === 'YEARLY' && '*'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.yearly_price || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, yearly_price: e.target.value ? parseFloat(e.target.value) : undefined }))}
                  className={`w-full rounded-md border ${errors.yearly_price ? 'border-red-300' : 'border-gray-300'} px-3 py-2 focus:border-blue-500 focus:ring-blue-500`}
                  placeholder="0.00"
                />
                {errors.yearly_price && <p className="text-red-500 text-sm mt-1">{errors.yearly_price}</p>}
              </div>
            )}

            {formData.billing_type === 'ONE_TIME' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  One-time Price ($) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.one_time_price || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, one_time_price: e.target.value ? parseFloat(e.target.value) : undefined }))}
                  className={`w-full rounded-md border ${errors.one_time_price ? 'border-red-300' : 'border-gray-300'} px-3 py-2 focus:border-blue-500 focus:ring-blue-500`}
                  placeholder="0.00"
                />
                {errors.one_time_price && <p className="text-red-500 text-sm mt-1">{errors.one_time_price}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Features</h3>

          {/* Add Feature Form */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                value={currentFeature.title}
                onChange={(e) => setCurrentFeature(prev => ({ ...prev, title: e.target.value }))}
                className="rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Feature title"
              />
              <input
                type="text"
                value={currentFeature.description}
                onChange={(e) => setCurrentFeature(prev => ({ ...prev, description: e.target.value }))}
                className="rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Feature description"
              />
            </div>

            <div className="flex items-center space-x-4 mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={currentFeature.included}
                  onChange={(e) => setCurrentFeature(prev => ({ ...prev, included: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Included</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={currentFeature.highlighted}
                  onChange={(e) => setCurrentFeature(prev => ({ ...prev, highlighted: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Highlighted</span>
              </label>
            </div>

            <Button type="button" variant="outline" onClick={addFeature}>
              Add Feature
            </Button>
          </div>

          {/* Features List */}
          {formData.features.length > 0 && (
            <div className="space-y-2">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{feature.title}</span>
                      {feature.highlighted && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">Highlighted</span>
                      )}
                      {!feature.included && (
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Not Included</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Technical Specifications */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Technical Specifications</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CPU Cores</label>
              <input
                type="number"
                value={formData.technical_specs.cpu_cores || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  technical_specs: {
                    ...prev.technical_specs,
                    cpu_cores: e.target.value ? parseInt(e.target.value) : undefined
                  }
                }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="4"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">RAM (GB)</label>
              <input
                type="number"
                value={formData.technical_specs.ram_gb || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  technical_specs: {
                    ...prev.technical_specs,
                    ram_gb: e.target.value ? parseInt(e.target.value) : undefined
                  }
                }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="8"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Storage (GB)</label>
              <input
                type="number"
                value={formData.technical_specs.storage_gb || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  technical_specs: {
                    ...prev.technical_specs,
                    storage_gb: e.target.value ? parseInt(e.target.value) : undefined
                  }
                }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bandwidth (GB)</label>
              <input
                type="number"
                value={formData.technical_specs.bandwidth_gb || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  technical_specs: {
                    ...prev.technical_specs,
                    bandwidth_gb: e.target.value ? parseInt(e.target.value) : undefined
                  }
                }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="1000"
              />
            </div>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.technical_specs.ssl_included || false}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  technical_specs: {
                    ...prev.technical_specs,
                    ssl_included: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">SSL Included</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.technical_specs.backup_included || false}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  technical_specs: {
                    ...prev.technical_specs,
                    backup_included: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Backup Included</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.technical_specs.monitoring_included || false}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  technical_specs: {
                    ...prev.technical_specs,
                    monitoring_included: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Monitoring Included</span>
            </label>
          </div>
        </div>

        {/* Google Workspace */}
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.includes_google_workspace || false}
                onChange={(e) => setFormData(prev => ({ ...prev, includes_google_workspace: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Includes Google Workspace</span>
            </label>

            {formData.includes_google_workspace && (
              <select
                value={formData.google_workspace_tier || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, google_workspace_tier: e.target.value }))}
                className="rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select tier</option>
                {googleWorkspaceTiers.map(tier => (
                  <option key={tier.value} value={tier.value}>{tier.label}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Package Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Package Settings</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Badge Text</label>
              <input
                type="text"
                value={formData.badge_text || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, badge_text: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Most Popular"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
              <input
                type="url"
                value={formData.image_url || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="https://example.com/image.png"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Featured</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_popular}
                onChange={(e) => setFormData(prev => ({ ...prev, is_popular: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Popular</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_available}
                onChange={(e) => setFormData(prev => ({ ...prev, is_available: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Available</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Package' : 'Create Package')}
          </Button>
        </div>
      </form>
    </div>
  )
}